// System
import { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import {
  EnumTableColumnHeader,
  StringTableColumnHeader,
  TableColumnHeader,
} from '@Components/shared/tables/columns';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';
import { RaindropItemDto } from '@contracts/api/dto/raindrop/RaindropDto';

// Utils
import {
  RaindropListColumns,
  RaindropListFilter,
  raindropListColumns,
} from '@utils/tables/raindrop/raindropListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';

import { useRaindropsTable } from '@Modal/hooks/raindrop/useRaindropsTable';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { parseISO } from 'date-fns';
import { numberFormatter } from '@Features/analytics/AnalyticsList/useAnalyticsTableColumns';
import { SingleDateTableColumnHeader } from '@Components/shared/tables/columns/DateTableColumnHeader/SingleDateTableColumnHeader';
import { useDispatch } from 'react-redux';
import { ClaimLimitPeriod } from '@Modal/reducers/raindrops/types';

type HeaderOptions = {
  filter: Filterable<RaindropListFilter>;
};

type Columns = TableColumnDescriptionArray<
  RaindropItemDto,
  HeaderOptions,
  typeof raindropListColumns
>;

type Column = TableColumnDescription<RaindropItemDto, HeaderOptions, RaindropListColumns>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useRaindropTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const dispatch = useDispatch();
  const { handleFilterSelect, handleOpenCancelRaindropModal, setRaindropTableFilters } =
    useRaindropsTable();
  const { userPermissions, currentUser } = useAuthorization();
  const permissionToCancelRaindrops =
    userPermissions?.includes('CAN_CANCEL_RAINDROP_CAMPAIGN') || currentUser?.isSuper;

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'raindrop.id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'id'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>{row?.id && <TruncateText text={row?.id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'raindrop.name',
          title: 'Name',
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'name'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.name}</Box>,
        },
        {
          name: 'raindrop.providerId',
          title: 'Provider ID',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'providerId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => (
            <>
              {row?.providerId && (
                <TruncateText text={row?.providerId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'raindrop.casinoId',
          title: 'Casino ID',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'casinoId'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.casinoId || ''}</Box>,
        },
        {
          name: 'raindrop.startDate',
          title: 'Start Date',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <SingleDateTableColumnHeader
              title={title}
              onApply={(date) => {
                date?.length
                  ? dispatch(
                      setRaindropTableFilters({
                        codeName: 'startDate',
                        value: date,
                      })
                    )
                  : null;
              }}
              onReset={() => {
                dispatch(
                  setRaindropTableFilters({
                    codeName: 'startDate',
                    value: undefined,
                  })
                );
              }}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.startDate && (
                <DateLabel date={new Date(parseISO(row?.startDate))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'raindrop.endDate',
          title: 'End Date',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <SingleDateTableColumnHeader
              title={title}
              onApply={(date) => {
                date?.length
                  ? dispatch(
                      setRaindropTableFilters({
                        codeName: 'endDate',
                        value: date,
                      })
                    )
                  : null;
              }}
              onReset={() => {
                dispatch(
                  setRaindropTableFilters({
                    codeName: 'endDate',
                    value: undefined,
                  })
                );
              }}
            />
          ),
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.endDate && <DateLabel date={new Date(parseISO(row?.endDate))} format="long" />}
            </Box>
          ),
        },
        {
          name: 'raindrop.currency',
          title: 'Currency',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <StringTableColumnHeader
              title={title}
              codeName={'currency'}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.currency}</Box>,
        },
        {
          name: 'raindrop.freebetValue',
          title: 'Freebet Value',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.freebetValue || 0)}</Box>
          ),
        },
        {
          name: 'raindrop.dayBudget',
          title: 'Day Budget',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{numberFormatter(row?.dayBudget || 0)}</Box>,
        },
        {
          name: 'raindrop.totalBudget',
          title: 'Total Budget',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.totalBudget || 0)}</Box>
          ),
        },
        // {
        //   name: 'raindrop.claimLimitNumber',
        //   title: 'Claim Rate',
        //   sx: { minWidth: '150px', maxWidth: '180px' },
        //   header: ({ title }) => <TableColumnHeader title={title} />,
        //   body: ({ row }) => (
        //     <Box sx={{ ...ellipsis }}>{numberFormatter(row?.claimLimitNumber || 0)}</Box>
        //   ),
        // },
        {
          name: 'raindrop.status',
          title: 'Status',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => (
            <EnumTableColumnHeader
              title={title}
              codeName={'status'}
              options={[
                { key: 'Active', value: 'ACTIVE' },
                { key: 'Pending', value: 'PENDING' },
                { key: 'Canceled', value: 'CANCELED' },
              ]}
              handleSetFilters={handleFilterSelect}
            />
          ),
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.status || ''}</Box>,
        },
        {
          name: 'raindrop.actions',
          title: 'Actions',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Button
              variant="text"
              size="small"
              disabled={row?.status === 'CANCELED' || !permissionToCancelRaindrops}
              onClick={() => handleOpenCancelRaindropModal(row?.id)}
            >
              Cancel
            </Button>
          ),
        },
        {
          name: 'raindrop.timezone',
          title: 'Timezone',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.timezone}</Box>,
        },
        {
          name: 'raindrop.distributionFrom',
          title: 'Distribution Time From',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.distributionFrom}</Box>,
        },
        {
          name: 'raindrop.distributionTo',
          title: 'Distribution Time To',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.distributionTo}</Box>,
        },
        {
          name: 'raindrop.freebetsPerRain',
          title: 'Number of Freebets Per Rain',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.freebetsPerRain || 0)}</Box>
          ),
        },
        {
          name: 'raindrop.rainsPerDay',
          title: 'Rain Counts Per Day',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.rainsPerDay || 0)}</Box>
          ),
        },
        {
          name: 'raindrop.claimLimitPeriod',
          title: 'Claim Limitation Period',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {ClaimLimitPeriod[row?.claimLimitPeriod as keyof typeof ClaimLimitPeriod] || ''}
            </Box>
          ),
        },
        {
          name: 'raindrop.minMultiplier',
          title: 'Min Multiplier',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.minMultiplier || 0)}</Box>
          ),
        },
        {
          name: 'raindrop.freebetValidityDate',
          title: 'Freebet Validity Date',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.freebetValidityDate && (
                <DateLabel date={new Date(parseISO(row?.freebetValidityDate))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'raindrop.claimDeadlineMinutes',
          title: 'Claim Deadline (minutes)',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.claimDeadlineMinutes || 0)}</Box>
          ),
        },
        {
          name: 'raindrop.playerMinBalance',
          title: 'Player Min Balance',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.playerMinBalance || 0)}</Box>
          ),
        },
        {
          name: 'raindrop.playerMinWager',
          title: 'Wager amount',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.playerMinWager || 0)}</Box>
          ),
        },
        {
          name: 'raindrop.wagerPeriod',
          title: 'Wager Period',
          sx: { minWidth: '150px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{numberFormatter(row?.wagerPeriod || 0)}</Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
